import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridCellProps,
  GridColumn as Column,
  GridRowClickEvent,
} from '@progress/kendo-react-grid'
import { ACCOUNT_DUNNING } from 'components/common/constants'
import { DropdownComponent, DropdownOption, OnChangeEvent } from 'components/dropdown/component'
import { DateTimeCell } from 'components/gridcells/datetime'
import { HeaderComponent } from 'components/header/component'
import { SearchIcon } from 'components/icons/searchicon'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import { EditCellProps} from 'generic/editCell'
import * as GenericRedux from 'generic/genericRedux'
import { RasaDataGrid } from 'generic/rasaDataGrid'
import * as GA from 'google-analytics'
import React, { Component } from 'react'
import { Button } from 'reactstrap'
import { Roles } from 'shared/constants'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import {UsageMeterComponent} from '../../shared/usage-meter-component'
import {UNLIMITED_VALUE} from '../../shared_server_client/constants'
import { getMaximumNumberOfNewsletters } from '../../shared_server_client/types/billing_plan'
import { SELECTED_COMMUNITY } from '../../store'
import { isLocal } from '../../utils'
import { FeatureUnavailableComponent, FeatureUnavailableComponentName } from '../feature-unavailable'
import {NEWSLETTER_ADDITIONAL_FEATURES} from '../upgrade/constants';
import './_styles.scss'
import * as GridCells from './gridCells'
import { EditNewsletterEditCell } from './kendocells/editNewsletterEditCell'
import { AddAccountModal, AddNewsletterModal, ExpirationModal } from './modals'
import { noMoreUpgradeAvailable } from 'generic/utility'
import { Dataset } from 'generic/dataset'
import { DEFAULT_PARTNER_IDENTIFIER } from 'shared_server_client/constants'

interface CommunitiesState {
  accountId?: number,
  canAddAccount: boolean,
  canAddNewsletters: boolean,
  communityId?: number,
  currentNewsletterCount: number,
  expirationDate?: Date,
  forceReload: boolean,
  hasFeatureAccess: boolean,
  isLoading: boolean,
  isNoMoreUpgradeAvailable: boolean,
  isPartnerAdmin: boolean,
  isSuperUser: boolean,
  loggedInUserId?: number,
  maxNewsletterCount: number,
  partners: DropdownOption[],
  partnerIdentifier: string,
  selectedPartner: DropdownOption,
  search: string,
  showGrid: boolean,
  skip: number,
  sort: SortDescriptor[],
  take: number,
}

type CommunitiesListProps = GenericRedux.AllComponentPropsWithModal<any>

abstract class BaseCommunitiesListComponent extends Component<CommunitiesListProps, CommunitiesState>  {
  public static contextType = RasaContext
  private newslettersSearchTypingTimer
  private newslettersSearchTypingTimerInterval = 500 // .5 seconds

  constructor(props: any) {
    super(props)
    this.state = {
      canAddAccount: false,
      canAddNewsletters: false,
      currentNewsletterCount: 0,
      forceReload: false,
      hasFeatureAccess: false,
      isPartnerAdmin: false,
      isSuperUser: false,
      isLoading: false,
      isNoMoreUpgradeAvailable: false,
      maxNewsletterCount: 0,
      partners: [],
      partnerIdentifier: DEFAULT_PARTNER_IDENTIFIER,
      selectedPartner: null,
      search: '',
      showGrid: false,
      skip: 0,
      sort: [],
      take: 25,
    }
  }

  public render() {
    return this.state.isLoading ? <Loading size="64" /> :
      (!this.state.hasFeatureAccess && !this.state.isSuperUser && !this.state.isPartnerAdmin) ?
        <FeatureUnavailableComponent {...this.featureUnavailableProps()} /> :
        <div className="communities">
          <AddAccountModal
            data={this.props.modals}
            closeModal={this.props.closeModal}
            saveModal={this.props.saveModal}/>
          <AddNewsletterModal
            data={this.props.modals}
            closeModal={this.props.closeModal}
            saveModal={this.props.saveModal}/>
          <ExpirationModal
            data={this.props.modals}
            closeModal={this.props.closeModal}
            saveModal={this.props.saveModal}
            saveText="Update"
            expiration_date={this.state.expirationDate}
            onSave={this.onAccountExpirationUpdate}
            title="Update Account"/>
          <div className="header-container">
            <HeaderComponent
              title={'Settings'}
              subTitle={'Your Newsletters'}
              description={['Select a newsletter']}
            />
            <div className="add-newsletter-wrapper">
              {this.renderUsageMeter()}
              {this.state.canAddAccount &&
                <Button className="add-button add-account"
                        onClick={() => this.props.openModal(AddAccountModal.key, null)}
                >
                + Add Account
                </Button>}
            </div>
          </div>
          <div className="search-bar-and-buttons-container">
            <div className="search-bar">
              <label>
                <SearchIcon />
                <input
                  type="text"
                  value={this.state.search}
                  placeholder="Search..."
                  onChange={(e) => {
                    const searchValue = e.target.value
                    clearTimeout(this.newslettersSearchTypingTimer)
                    this.setState({showGrid: false}, () => {
                      this.setSearch(searchValue)
                      this.newslettersSearchTypingTimer = setTimeout(this.onNewslettersSearchTypingDone,
                        this.newslettersSearchTypingTimerInterval)
                    })
                  }}
                />
              </label>
            </div>
            {this.state.isSuperUser && this.state.partners.length > 0 && (
              <div className="partner-dropdown">
                <label className="partner-label">Partner:</label>
                <DropdownComponent
                  data={this.state.partners}
                  selected={this.state.selectedPartner ? this.state.selectedPartner.key : this.state.partnerIdentifier}
                  onChange={(e: OnChangeEvent) => {
                    this.setState({
                      partnerIdentifier: String(e.selected.key),
                      selectedPartner: e.selected,
                      showGrid: false,
                    }, () => {
                      this.setState({showGrid: true})
                    })
                  }}
                />
              </div>
            )}
          </div>
          {!this.state.showGrid
            ?
            <Loading size="64" />
            :
            <div className="clickable-item">
              {this.renderGrid()}
            </div>
          }
        </div>
  }

  protected renderUsageMeter = () => {
    const isUnlimited: boolean = this.state.maxNewsletterCount === UNLIMITED_VALUE
      || this.state.maxNewsletterCount === -1
    const currentNewslettersCount: number = this.state.currentNewsletterCount
    const maxNewslettersCount: number = this.state.maxNewsletterCount
    const isLimitReached: boolean = currentNewslettersCount >= maxNewslettersCount
    const disableActionButton: boolean = !this.state.canAddNewsletters
    const description: string = 'You\'ve reached your newsletters limit, please upgrade your plan to add a new newsletter.'
    const handleClickEvent = () => {
      this.props.openModal(AddNewsletterModal.key, null)
    }

    return <UsageMeterComponent
      actionButton={{
        disabled: disableActionButton,
        callback: handleClickEvent,
        label: '+ Add newsletter'
      }}
      entity='newsletters'
      isSuperUser={this.state.isSuperUser}
      isUnlimited={isUnlimited}
      isLimitReached={isLimitReached}
      currentCount={currentNewslettersCount}
      maxLimit={maxNewslettersCount}
      source={GA.UpgradeSource.AddNewsletter}
      progressDescription={description}
      showContactToRasa={this.state.isNoMoreUpgradeAvailable}
      progressText='Newsletters Used' />
  }

  protected abstract renderGrid(): any

  protected MyLogoCell = (props: GridCellProps) => {
    return <GridCells.SelectCell {...props} onClick={this.selectCommunity} type={GridCells.SelectCellType.Logo}/>
  }

  protected MySelectCell = (props: GridCellProps) => {
    return <GridCells.SelectCell disabled={!this.canSelectCommunity(props.dataItem)}
      {...props} onClick={this.selectCommunity} type={GridCells.SelectCellType.Button}/>
  }

  protected MyNameAndDateCreatedCell = (props: GridCellProps) => {
    return <GridCells.NameAndCreatedCell {...props} onClick={this.selectCommunity}/>
  }

  protected MyExpirationCell = (props: GridCellProps) => {
    return <GridCells.ExpirationCell {...props} onClick={this.changeCommunity} />
  }

  protected selectCommunity = (selected: any) => {
    if (this.canSelectCommunity(selected)) {
        this.setState({isLoading: true})
        if (selected.dashboard_alternate_domain && !isLocal()) {
        // whitelist domain is setup for this newsletter. redirect to its website
        window.location.href = `https://${selected.dashboard_alternate_domain}?${SELECTED_COMMUNITY}=${selected.community_identifier}`
      } else {
        this.context.user.switchCommunity(selected.community_identifier).then(() => {
          this.setState({isLoading: false})
          window.location.href = '/'
        })
      }
    }
  }

  private canSelectCommunity = (selected: any) => {
    return (!selected.expiration_date || new Date(selected.expiration_date) > new Date())
  }

  protected changeCommunity = (selected: any) => {
    this.setState({
      accountId: selected.community_account_id,
      communityId: selected.community_id,
      expirationDate: selected.community_expiration_date,
    }, () => {
      this.props.openModal(ExpirationModal.key, null);
    })
  }

  private onAccountExpirationUpdate = (expirationDate) => {
    return this.context.entityMetadata
    .getEntityObject('account')
    .then((entity) => {
      entity.setRecordId(this.state.communityId, this.state.accountId)
      entity.data.expiration_date = expirationDate
      return entity.save().then((e) => {
        this.setState({forceReload: true}, () => this.setState({forceReload: false}));
        this.props.closeModal(ExpirationModal.key, null);
      })
    })
  }

  private onNewslettersSearchTypingDone = () => {
    this.setState({showGrid: true})
  }

  private setSearch = (s: string) => {
   this.setState({
     search: s,
   })
  }

  private featureUnavailableProps = () => {
    return {
      loadComponent: FeatureUnavailableComponentName.Upgrade,
      source: GA.UpgradeSource.AddNewsletter,
      upgradeComponentProps: {
        source: GA.UpgradeSource.AddNewsletter,
        features: NEWSLETTER_ADDITIONAL_FEATURES,
        description: 'Unlock more templating and features when you upgrade your plan',
      },
    }
  }
}

class CommunitiesListComponent extends BaseCommunitiesListComponent {

  private communitiesData = []
  private sharedStore: SharedStore
  constructor(props: any) {
    super(props)
  }

  public componentDidMount = () => {
    this.sharedStore = SharedStore.instance(this.context)
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
      this.sharedStore.getValue(SharedKeys.role),
    ])
    .then(([activeCommunity, role]) => {
      if (activeCommunity.billingInfo && activeCommunity.billingInfo.usageStats) {
        const maximumNumberOfNewsletter = getMaximumNumberOfNewsletters(activeCommunity.billingInfo.currentPlan)
        this.setState({
          isNoMoreUpgradeAvailable: noMoreUpgradeAvailable(activeCommunity.billingInfo.currentPlan),
          canAddNewsletters: !!activeCommunity.billingInfo.usageStats.canAddNewsletters,
          currentNewsletterCount: activeCommunity.billingInfo.usageStats.numNewsletters,
          maxNewsletterCount: maximumNumberOfNewsletter,
          hasFeatureAccess: maximumNumberOfNewsletter > 1
            || maximumNumberOfNewsletter === -1
            || maximumNumberOfNewsletter === UNLIMITED_VALUE,
        })
      }
      if (activeCommunity.role === Roles.super_admin || activeCommunity.role === Roles.partner_admin) {
        this.setState({canAddAccount: true})
      }
      this.setState({
        isSuperUser: role === Roles.super_admin,
        isPartnerAdmin: role === Roles.partner_admin,
        communityId: activeCommunity.communityId,
        partnerIdentifier: activeCommunity.billingInfo.productSubscription.account_partner_identifier,
        showGrid: true,
      }, () => {
        if (this.state.isSuperUser) {
          this.loadPartners()
        }
      })
    })
  }

  public renderGrid() {
    return <RasaDataGrid
            // Rasa Properties for entity, datasetName etc
            entityName="identityCommunity"
            datasetName="userNewsletters"
            datasetParams={[
              { param: 'filterVal', value: this.state.search },
              { param: 'partnerIdentifier', value: this.state.partnerIdentifier },
            ]}
            onItemChange={this.itemChanged}
            forceReload={this.state.forceReload}
            editDefaultState={false}
            onDataReceived={this.dataReceived}
            onRowClick={(e: GridRowClickEvent) =>
              this.selectCommunity(e.dataItem)
            }
            // events
            sortable={true}
            pageable={true}
            pageSize={25}
            gridFilter={this.filterData}
            data={[]}
          >
            <Column
              field="brand_header_image_url"
              title="Logo"
              cell={this.MyLogoCell}
              width={150}
            />
            <Column
              field="c.name"
              title={'Name & Date Created'}
              cell={this.MyNameAndDateCreatedCell}
            />
            {(this.state.isSuperUser || this.state.isPartnerAdmin) &&
            <Column
              field="account_name"
              title="Account Name"
            />}
            <Column
              field="last_actual"
              title="Last Sent #"
            />
            <Column
              field="last_sent"
              title="Last Send"
              cell={DateTimeCell}
            />
            {this.state.isSuperUser &&
            <Column
              field="billing_plan_name"
              title="Billing Plan"
            />}
            {this.state.isSuperUser &&
            <Column
              title="Identifier"
              cell={GridCells.MyCommunityIdentifier}
            />}
            <Column
              cell={this.CommunitiesEditCell}
              width={50}
            />
          </RasaDataGrid>
  }
  public CommunitiesEditCell = (props: EditCellProps) => {
    const activeCommunities = this.communitiesData.filter((f) => f.is_active)
    const hideDots = this.state.isSuperUser ? false : (!props.dataItem.is_archived && activeCommunities.length === 1)
    return this.communitiesData.length > 1 || (this.state.isSuperUser && this.communitiesData.length > 0) ?
      <EditNewsletterEditCell {...props}
        hideDots={hideDots}
        canArchive={!props.dataItem.is_archived && (this.state.isSuperUser || activeCommunities.length > 1)}
        canCancelAccount={this.state.isSuperUser && !props.dataItem.account_is_cancelled}
        canRemoveCancellation={this.state.isSuperUser && props.dataItem.account_is_cancelled
          && props.dataItem.community_account_suspended
          && props.dataItem.community_account_suspend_reason === ACCOUNT_DUNNING}
        canSuspendAccount={this.state.isSuperUser && !props.dataItem.community_account_suspended
          && !props.dataItem.account_is_cancelled}
        canRemoveSuspension ={this.state.isSuperUser && props.dataItem.community_account_suspended
          && props.dataItem.community_account_suspend_reason !== ACCOUNT_DUNNING
          && !props.dataItem.account_is_cancelled}
        canEnableSendAccount={this.state.isSuperUser && !props.dataItem.account_is_cancelled
          && !props.dataItem.community_account_suspended}
        canRestore={props.dataItem.is_archived}
        onChangeExpiration={this.changeCommunity}
      /> : null
  }

  protected dataReceived = (data) => {
    if (data.data) {
      this.communitiesData = data.data;
    }
  }
  protected itemChanged = (event) => {
    if (event.field === 'is_archived') {
      this.communitiesData.find((d) => d.id === event.dataItem.id).is_active = !event.value
      this.setState({
        currentNewsletterCount: this.state.currentNewsletterCount - 1
      })
    }
  }
  private filterData = (a: any): boolean => {
    return !a.community_identifier.endsWith('-defaults') || this.state.isSuperUser
  }

  private loadPartners = () => {
    if (!this.state.communityId) return

    new Dataset().loadCommunityDataset('partners', String(this.state.communityId))
    .then((result) => {
      const partners = result[0].map((partner) => {
        return {
          key: partner.identifier,
          value: partner.identifier,
          description: partner.name,
        }
      })
      this.setState({
        partners,
        selectedPartner: partners.find(p => p.key === this.state.partnerIdentifier) || partners[0],
      })
    })
  }
}

export const CommunitiesList = GenericRedux.registerNewComponentWithModals<any>(
  CommunitiesListComponent,
  'communities_list',
  [AddNewsletterModal.key, ExpirationModal.key, AddAccountModal.key],
  {},
)
